/**
 * Maps routes to allowed roles for role-based access control
 * 
 * Roles available: ADMIN, DCS, CHECKER, APPROVER, UNION, CB_ADMIN, CB_USER, DEPTADMIN, etc.
 */
// Define role groups for easier management
export const ROLES = {
  ADMIN: "ADMIN",
  CB_ADMIN: "CB_ADMIN",
  CB_USER: "CB_USER",
  DEPTADMIN: "DEPTADMIN",
  DCS: "DCS",
  CHECKER: "CHECKER",
  APPROVER: "APPROVER",
  UNION: "UNION",
};

export const ENTITY_TYPES = {
  DCS: "DCS",
  CHECKER: "CHECKER",
  APPROVER: "APPROVER",
  UNION: "UNION",
};

export const ADMIN_ROLES = [ROLES.ADMIN, ROLES.CB_ADMIN, ROLES.CB_USER, ROLES.DEPTADMIN];
export const MAKER_ROLES = [ROLES.DCS];
export const VERIFICATION_ROLES = [ROLES.CHECKER, ROLES.APPROVER];
export const UNION_ROLES = [ROLES.UNION];
export const ALL_ROLES = Object.values(ROLES);

/**
 * Route to role mapping
 * Pattern matching:
 * - Exact match: "/admin/dashboard"
 * - Prefix match: "/admin/*" - matches all routes starting with /admin/
 */
export const ROUTE_PERMISSIONS = {
  // ADMIN ROUTES
  "/admin/data-dashboard": [...ADMIN_ROLES, ...UNION_ROLES],
  "/dashboard/cctv": ADMIN_ROLES,
  "/dashboard/milkprocurementdat": ADMIN_ROLES,
  "/dashboard/qualitymonitoring": [...ADMIN_ROLES, ...UNION_ROLES],
  "/dashboard/qualitymonitoringdat": [...ADMIN_ROLES, ...UNION_ROLES],
  "/dashboard/dcswiseprocreport": [...ADMIN_ROLES, ...UNION_ROLES],
  "/dashboard/dcswiseprocreportdat": [...ADMIN_ROLES, ...UNION_ROLES],
  "/dashboard/farmerledgerreport": [...ADMIN_ROLES, ...UNION_ROLES],
  "/dashboard/sappurchaseorder": ADMIN_ROLES,
  "/dashboard/sappurchaseorder/*": ADMIN_ROLES,
  "/master/sapmaterial": ADMIN_ROLES,
  "/admin/dashboard": ADMIN_ROLES,
  "/admin/dashboard/maker-details": ADMIN_ROLES,
  "/admin/dashboard/checker-details": ADMIN_ROLES,
  "/admin/dashboard/approver-details": ADMIN_ROLES,
  "/admin/dcs": ADMIN_ROLES,
  "/admin/dcs/*": ADMIN_ROLES,
  "/admin/farmer": ADMIN_ROLES,
  "/admin/farmer/*": ADMIN_ROLES,
  "/admin/create-group": ADMIN_ROLES,
  "/admin/create-user": ADMIN_ROLES,
  "/admin/edit-user": ADMIN_ROLES,
  "/icccmaster/vehiclemaster": [...ADMIN_ROLES, ...UNION_ROLES],

  // CHECKER ROUTES
  "/checker/dashboard": [ROLES.CHECKER],
  "/checker/farmer-list": [ROLES.CHECKER],
  "/checker/farmer-list/*": [ROLES.CHECKER],
  "/checker/profile": [ROLES.CHECKER],
  "/checker/self-profiling": [ROLES.CHECKER],

  // APPROVER ROUTES 
  "/approver/dashboard": [ROLES.APPROVER],
  "/approver/farmer-list": [ROLES.APPROVER],
  "/approver/farmer-list/*": [ROLES.APPROVER],
  "/approver/profile": [ROLES.APPROVER],
  "/approver/self-profiling": [ROLES.APPROVER],

  //DCS ROUTES
  "/maker/dashboard": MAKER_ROLES,
  "/master/dcsmaster": [...MAKER_ROLES, ...ADMIN_ROLES],
  "/master/dcsmaster/*": [...MAKER_ROLES, ...ADMIN_ROLES],
  "/master/farmermaster": [...MAKER_ROLES, ...ADMIN_ROLES],
  "/master/farmermaster/*": [...MAKER_ROLES, ...ADMIN_ROLES],
  "/setting/dcs-secretary-details": MAKER_ROLES,
  "/dcs/daily-report": MAKER_ROLES,
  "/master/dcsmaster/add-dcs": [...MAKER_ROLES, ...ADMIN_ROLES],
  "/master/dcsmaster/add-dcs/*": [...MAKER_ROLES, ...ADMIN_ROLES],


  // UNION ROUTES 
  "/union/admin/dashboard": UNION_ROLES,
  "/union/admin/farmer-list": UNION_ROLES,
  "/union/admin/dcs-list": UNION_ROLES,
  "/union/admin/*": UNION_ROLES,

  // MILK PROCUREMENT — shared between APPROVER and UNION
  "/milk-procurement/farmer-list/view-dcs/:routeDcsId": [ROLES.APPROVER, ROLES.CHECKER, ...ADMIN_ROLES, ...UNION_ROLES],
  "/milk-procurement/farmer-list/view-dcs/*": [ROLES.APPROVER, ROLES.CHECKER, ...ADMIN_ROLES, ...UNION_ROLES],


  // SHARED ADMIN ROUTES 
  // These routes are accessible by ADMIN and other privileged roles
  "/dashboard": [...ADMIN_ROLES, ...MAKER_ROLES],
  "/master/unionmaster": ADMIN_ROLES,
  "/master/unionmaster/*": ADMIN_ROLES,
  "/master/plantmaster": ADMIN_ROLES,
  "/master/plantmaster/*": ADMIN_ROLES,
  "/master/bmcmaster": ADMIN_ROLES,
  "/master/bmcmaster/*": ADMIN_ROLES,
  "/master/ratemaster": ADMIN_ROLES,
  "/master/users": ADMIN_ROLES,
  "/master/users/*": ADMIN_ROLES,

  // ICCC ROUTES 
  "/icccmaster/vehiclemaster/*": [...ADMIN_ROLES, ...UNION_ROLES],
  "/icccmaster/*": ADMIN_ROLES,
  "/iccc/*": ADMIN_ROLES,
  "/live-gps-tracking": ADMIN_ROLES,
  "/trip-navigator": ADMIN_ROLES,
  "/trip-navigator/*": ADMIN_ROLES,

  //SETTINGS & MANAGEMENT
  "/setting": [...ADMIN_ROLES, ...MAKER_ROLES, ROLES.CHECKER, ROLES.APPROVER],
  "/user-role-management": ADMIN_ROLES,
  "/user-role-management/*": ADMIN_ROLES,

  //REPORTS 
  "/reports/makerreport": [...ADMIN_ROLES, ...UNION_ROLES],
  "/reports/dailyreport": [...ADMIN_ROLES, ...UNION_ROLES],
  "/reports/unionwiwsereport": [...ADMIN_ROLES, ...UNION_ROLES],
  "/reports/dcs-daily-report": [...ADMIN_ROLES, ...UNION_ROLES],
};

/**
 * Routes where dark mode is available. The theme toggle only appears on these
 */
export const DARK_MODE_ROUTES = [
  "/admin/data-dashboard",
  "/dashboard/milkprocurementdat",
  "/dashboard/qualitymonitoring",
  "/dashboard/qualitymonitoringdat",
  "/dashboard/dcswiseprocreport",
  "/dashboard/dcswiseprocreportdat",
  "/dashboard/farmerledgerreport",
  "/union/admin/dashboard",
  "/union/admin/dashboard/procurement",
  "/icccmaster/vehiclemaster",
  "/dashboard/unionsale",
  "/dashboard/sappurchaseorder",
  "/master/sapmaterial"
];

/**
 * Whether dark mode is allowed for the given role on the given route.
 * Requires an admin or union role AND an eligible route.
 * @param {string} role - User role
 * @param {string} pathname - Current route pathname
 * @returns {boolean}
 */
export const isDarkModeAllowed = (role, pathname) => {
  if (!role || !pathname) return false;
  const isAllowedRole = [...ADMIN_ROLES, ...UNION_ROLES]
    .map((r) => r.toUpperCase())
    .includes(role.toUpperCase());
  if (!isAllowedRole) return false;
  return DARK_MODE_ROUTES.some(
    (p) => pathname === p || pathname.startsWith(p + "/")
  );
};

/**
 * Get allowed roles for a route
 * @param {string} pathname - Current route pathname
 * @returns {string[]} Array of allowed roles, or empty array if no restriction
 */
export const getAllowedRoles = (pathname) => {
  // First try exact match
  if (ROUTE_PERMISSIONS[pathname]) {
    return ROUTE_PERMISSIONS[pathname];
  }
  // Try prefix matching (for wildcard routes)
  for (const [pattern, roles] of Object.entries(ROUTE_PERMISSIONS)) {
    if (pattern.endsWith("/*")) {
      const prefix = pattern.slice(0, -2);
      if (pathname.startsWith(prefix)) {
        return roles;
      }
    }
  }
  // No specific permission found - allow authenticated users
  return null;
};
/**
 * Check if a role is allowed to access a route
 * @param {string} role - User role
 * @param {string} pathname - Route pathname
 * @returns {boolean} True if allowed
 */
export const isRoleAllowed = (role, pathname) => {
  if (!role) return false;

  const allowedRoles = getAllowedRoles(pathname);
  if (!allowedRoles) return true;
  const normalizedRole = role.toUpperCase();
  return allowedRoles.map(r => r.toUpperCase()).includes(normalizedRole);
};
/**
 * Get redirect path based on role when access is denied
 * @param {string} role - User role
 * @returns {string} Redirect path
 */
export const getDefaultDashboard = (role) => {
  if (!role) return "/";
  const normalizedRole = role.toUpperCase();
  switch (normalizedRole) {
    case ROLES.ADMIN:
    case ROLES.DEPTADMIN:
      return "/admin/data-dashboard";
    case ROLES.CB_ADMIN:
    case ROLES.CB_USER:
      return "/admin/data-dashboard";
    case ROLES.DCS:
      return "/maker/dashboard";
    case ROLES.CHECKER:
      return "/checker/dashboard";
    case ROLES.APPROVER:
      return "/approver/dashboard";
    case ROLES.UNION:
      return "/union/admin/dashboard";
    default:
      return "/dashboard";
  }
};
export default {
  ROLES,
  ROUTE_PERMISSIONS,
  DARK_MODE_ROUTES,
  isDarkModeAllowed,
  getAllowedRoles,
  isRoleAllowed,
  getDefaultDashboard,
};
